/// <amd-module name="TFS/Admin/Groups/Common/Redux/Actions/GroupMembersPivotActions" />
import { ActionsUnion, createAction } from "redux/Utils/ActionHelper";
import { IOperationStatus } from "TFS/Admin/Common/Constants/Constants";
import { IPermissionPage, MemberOperation, MembershipQuery, SearchStatus as SearchGroupMembers } from "TFS/Admin/Groups/Common/Constants";
import { PermissionsGroup, QueryMembers, SelectableGraphMember } from "TFS/Admin/Groups/Common/Contracts";
import { IOrganizationAadData } from "TFS/Admin/OrganizationAadCommon/Contracts/DataModels";
import { ObservableArray } from "VSS/Core/Observable";
import { GraphUser } from "VSS/Platform/Contracts/Graph";
import { IIdentity } from "VSSUI/IdentityPicker";
import { MessageCardSeverity } from "VSSUI/MessageCard";

/**
 * Actions for permissions view
 */
export const GroupMembersPivotActions = {
    // Member pivot iniitializers
    initView: (selectedGroup: PermissionsGroup, permissionPage: IPermissionPage) =>
        createAction(GroupMembersPivotActionTypes.InitView, { selectedGroup, permissionPage }),
    fetchOrgSettings: () => createAction(GroupMembersPivotActionTypes.FetchOrgSettings),
    fetchOrgSettingsCompleted: (orgSettings: IOrganizationAadData) =>
        createAction(GroupMembersPivotActionTypes.FetchOrgSettingsCompleted, orgSettings),
    initialUserFetch: (members: ObservableArray<SelectableGraphMember>) => createAction(GroupMembersPivotActionTypes.InitialUserFetch, members),
    membersLoadCompleted: () => createAction(GroupMembersPivotActionTypes.GroupMembersLoadCompleted),

    // Filter Member actions
    searchMembers: (members: ObservableArray<SelectableGraphMember>, permissionPage: IPermissionPage, newKeyword?: string, oldKeyword?: string) =>
        createAction(GroupMembersPivotActionTypes.SearchGroupMembers, { members, permissionPage, newKeyword, oldKeyword }),
    updateSearchStatus: (searchStatus: SearchGroupMembers) => createAction(GroupMembersPivotActionTypes.UpdateSearchStatus, searchStatus),
    updateSearchResult: (keyword: string, result: any[], hasMore: boolean) =>
        createAction(GroupMembersPivotActionTypes.UpdateSearchResult, { result, hasMore, keyword }),

    //Delete Member actions
    onDeleteMemberClick: (member: SelectableGraphMember) => createAction(GroupMembersPivotActionTypes.DeleteGroupMemberClick, member),
    onDeleteDialogCancel: () => createAction(GroupMembersPivotActionTypes.DeleteDialogCancel),
    onDeleteDialogConfirm: (permissionsGroup: PermissionsGroup, member: SelectableGraphMember, numberOfAdmins: number) =>
        createAction(GroupMembersPivotActionTypes.DeleteDialogConfirm, { permissionsGroup, member, numberOfAdmins }),
    onMultiDeleteClick: (permissionsGroup: PermissionsGroup, members: SelectableGraphMember[], numberOfAdmins: number) =>
        createAction(GroupMembersPivotActionTypes.MultiDeleteClick, { permissionsGroup, members, numberOfAdmins }),

    onDismissMessageCard: () => createAction(GroupMembersPivotActionTypes.DismissMessageCard),
    onDismissMultiSummary: () => createAction(GroupMembersPivotActionTypes.DismissMultiSummary),
    memberOperationStatus: (
        operation: MemberOperation,
        resultMessage: string,
        severity: MessageCardSeverity,
        selectedItems?: SelectableGraphMember[],
        multiOperationStatuses?: IOperationStatus[]
    ) =>
        createAction(GroupMembersPivotActionTypes.GroupMemberOperationStatus, {
            operation,
            resultMessage,
            severity,
            selectedItems,
            multiOperationStatuses
        }),
    // Add member actions
    closeAddMemberPanel: () => createAction(GroupMembersPivotActionTypes.CloseAddGroupMemberPanel),
    openAddMemberPanel: () => createAction(GroupMembersPivotActionTypes.OpenAddGroupMemberPanel),
    onSaveMembers: (
        subjectIdentities: IIdentity[],
        selectedGroup: PermissionsGroup,
        isExpandedQuery: boolean,
        scopeDescriptor: string | undefined,
        permissionPage: IPermissionPage,
        supportServicePrincipals: boolean
    ) =>
        createAction(GroupMembersPivotActionTypes.SaveGroupMembers, {
            subjectIdentities,
            selectedGroup,
            scopeDescriptor,
            isExpandedQuery,
            permissionPage,
            supportServicePrincipals
        }),

    // Add & remove identities actions
    checkMembershipExistence: (member: IIdentity, groupDescriptor: string) =>
        createAction(GroupMembersPivotActionTypes.CheckMembershipExistence, { member, groupDescriptor }),
    addInvalidMember: (member: IIdentity) => createAction(GroupMembersPivotActionTypes.AddInvalidGroupMember, { member }),
    addValidMember: (member: IIdentity) => createAction(GroupMembersPivotActionTypes.AddValidGroupMember, { member }),
    removeMembers: (members: IIdentity[]) => createAction(GroupMembersPivotActionTypes.RemoveGroupMembers, { members }),

    // Change membership query actions
    changeMembershipQuery: (selectedGroup: PermissionsGroup, membershipQuery: MembershipQuery, queryMembers: QueryMembers) =>
        createAction(GroupMembersPivotActionTypes.ChangeMembershipQuery, { selectedGroup, membershipQuery, queryMembers }),
    changeMembershipQueryCompleted: (query: MembershipQuery, members: ObservableArray<SelectableGraphMember>) =>
        createAction(GroupMembersPivotActionTypes.ChangeMembershipQueryCompleted, { members, query }),

    checkMaterializedMember: (selectedUser: GraphUser) => createAction(GroupMembersPivotActionTypes.CheckMaterializedGroupMember, { selectedUser })
};

export const enum GroupMembersPivotActionTypes {
    InitView = "GroupMembersPivot/Init",
    InitialUserFetch = "GroupMembersPivot/InitialUserFetch",
    GroupMembersLoadCompleted = "GroupMembersPivot/GroupMembersLoadCompleted",
    FetchOrgSettings = "GroupMembersPivot/FetchOrgSettings",
    FetchOrgSettingsCompleted = "GroupMembersPivot/FetchOrgSettingsCompleted",

    // Filter
    SearchGroupMembers = "GroupMembersPivot/SearchGroupMembers",
    UpdateSearchStatus = "GroupMembersPivot/UpdateSearchStatus",
    UpdateSearchResult = "GroupMembersPivot/UpdateSearchResult",

    //Delete
    DeleteGroupMemberClick = "GroupMembersPivot/DeleteGroupMemberClick",
    DeleteDialogCancel = "GroupMembersPivot/DeleteDialogCancel",
    DeleteDialogConfirm = "GroupMembersPivot/DeleteDialogConfirm",
    MultiDeleteClick = "GroupMembersPivot/DeleteGroupMembersClick",

    DismissMessageCard = "GroupMembersPivot/DismissMessageCard",
    GroupMemberOperationStatus = "GroupMembersPivot/MemberOperationStatus",
    DismissMultiSummary = "GroupMembersPivot/DismissMultiSummary",

    // Add member
    SaveGroupMembers = "GroupMembersPivot/SaveGroupMembers",
    CloseAddGroupMemberPanel = "PermissionsView/CloseAddGroupMemberPanel",
    OpenAddGroupMemberPanel = "PermissionsView/OpenAddGroupMemberPanel",

    // Add & remove identities
    CheckMembershipExistence = "PermissionsView/CheckMembershipExistence",
    AddInvalidGroupMember = "PermissionsView/AddInvalidGroupMember",
    AddValidGroupMember = "PermissionsView/AddValidGroupMember",
    RemoveGroupMembers = "PermissionsView/RemoveGroupMembers",

    // Membership Query - Direct & Expanded
    ChangeMembershipQuery = "GroupMembersPivot/ChangeMembershipQuery",
    ChangeMembershipQueryCompleted = "GroupMembersPivot/ChangeMembershipQueryCompleted",

    CheckMaterializedGroupMember = "GroupMembersPivot/CheckMaterializedGroupMember"
}

export type GroupMembersPivotActions = ActionsUnion<typeof GroupMembersPivotActions>;
